import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FormActions } from "@jbnl/shared-ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  localSignInMessage,
  signInWithPassword,
  submitCode,
} from "./localAccount";
import { resetSession } from "./resetSession";

type Step = "wachtwoord" | "code";

/**
 * The state and the two network steps, apart from the rendering: `LocalSignIn` only ever reads
 * `step` to decide which form to draw.
 */
function useAanmeldFormulier() {
  const { t } = useTranslation(["session", "common"]);
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>("wachtwoord");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function submitPassword(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);

    try {
      const result = await signInWithPassword(email, password);

      // Nothing is signed in yet: the code is the second of two steps, always.
      if (result.status === "code") {
        setPassword("");
        setStep("code");
        return;
      }

      setError(localSignInMessage(result.status));
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : t("local.errors.signInFailed"),
      );
    } finally {
      setBusy(false);
    }
  }

  async function submitTwoFactor(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);

    try {
      const result = await submitCode(code);

      if (result.status === "aangemeld") {
        await resetSession(queryClient);
        return;
      }

      setCode("");
      setError(localSignInMessage(result.status));
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : t("local.errors.signInFailed"),
      );
    } finally {
      setBusy(false);
    }
  }

  return {
    step,
    email,
    setEmail,
    password,
    setPassword,
    code,
    setCode,
    error,
    busy,
    submitPassword,
    submitTwoFactor,
  };
}

/**
 * Password, then a code, never fewer steps. No registration link: local accounts are issued by
 * the wedstrijdleiders (docs/adr/0020-local-accounts.md).
 */
export function LocalSignIn() {
  const { t } = useTranslation(["session", "common"]);
  const {
    step,
    email,
    setEmail,
    password,
    setPassword,
    code,
    setCode,
    error,
    busy,
    submitPassword,
    submitTwoFactor,
  } = useAanmeldFormulier();

  if (step === "code") {
    return (
      <form
        onSubmit={(event) => void submitTwoFactor(event)}
        className="max-w-sm space-y-3"
        data-testid="local-two-factor"
      >
        <div className="grid gap-2">
          <Label htmlFor="code">{t("local.code.label")}</Label>
          <Input
            id="code"
            className="w-40 font-mono"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            inputMode="numeric"
            autoComplete="one-time-code"
            required
          />
        </div>
        <p className="text-xs text-muted-foreground">
          {t("local.code.recoveryHint")}
        </p>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <FormActions>
          <Button type="submit" disabled={busy}>
            {busy ? t("common:actions.busy") : t("local.submit")}
          </Button>
        </FormActions>
      </form>
    );
  }

  return (
    <form
      onSubmit={(event) => void submitPassword(event)}
      className="max-w-sm space-y-3"
      data-testid="local-signin"
    >
      <div className="grid gap-2">
        <Label htmlFor="email">{t("local.email")}</Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="username"
          required
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="password">{t("local.password")}</Label>
        <Input
          id="password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          required
        />
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <FormActions>
        <Button type="submit" disabled={busy}>
          {busy ? t("common:actions.busy") : t("local.submit")}
        </Button>
      </FormActions>
      <p className="text-xs text-muted-foreground">
        {t("local.noAccount.before")}
        <a
          href={t("common:portaalUrl")}
          target="_blank"
          rel="noreferrer"
          className="underline underline-offset-2 hover:text-foreground"
        >
          {t("local.noAccount.portaal")}
        </a>
        {t("local.noAccount.between")}
        <a
          href={`mailto:${t("common:mailbox")}`}
          className="underline underline-offset-2 hover:text-foreground"
        >
          {t("common:mailbox")}
        </a>
        {t("local.noAccount.after")}
      </p>
    </form>
  );
}
